/**
 * Auditoría del tenant — issue #45 (B6.9).
 *
 * Dos cosas, y la primera manda: **si la cadena de hash está íntegra**.
 * `audit_log` es un registro encadenado (cada fila lleva el hash de la
 * anterior) y `GET /api/v1/audit/verify` lo recalcula entero en la base. Un
 * auditor farmacéutico no viene a leer entradas sueltas: viene a comprobar
 * que nadie editó el pasado, y esa comprobación tiene que poder hacerla desde
 * la pantalla, sin pedirle una consulta SQL a nadie.
 *
 * La API devuelve **200 con `ok: false`** cuando la cadena no cierra: no es
 * un error de la petición, es un hallazgo. Acá se muestra como tal — en rojo,
 * con el motivo y con la primera entrada afectada — y esa entrada queda
 * marcada en el listado de abajo. "Dónde se rompe" es la primera pregunta
 * que sigue.
 *
 * El **ancla** (`GET /audit/head`: `seq` + `row_hash`) es lo que el auditor
 * se lleva anotado. Si en la visita siguiente esa entrada sigue teniendo ese
 * hash, todo lo anterior es inalterable sin producir una colisión de sha256
 * — incluso para quien administra nuestra base.
 *
 * Rol: la rama entera es `admin` (`routes.tsx`). La auditoría dice qué hizo
 * cada empleado del cliente; no es un tablero para todo el equipo.
 */
import type { UseQueryResult } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import { useApiQuery, useKeysetQuery, type KeysetResult } from '@/api/hooks';
import type { ApiError } from '@/api/errors';
import { auditKeys, type AuditListFilters } from '@/api/keys';
import type { components } from '@/api/schema';
import { useI18n } from '@/i18n/useI18n';
import type { MessageKey } from '@/i18n/messages';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { cn } from '@/ui/cn';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { AdminNav } from './AdminNav';

type AuditEntry = components['schemas']['AuditEntryOut'];
type Verification = components['schemas']['VerificationOut'];

const PAGE_SIZE = 25;

/** Los motivos que `audit_verify()` sabe distinguir. */
const REASON_KEYS: Record<string, MessageKey> = {
  contenido_alterado: 'audit.chain.reason.contenido_alterado',
  enlace_roto: 'audit.chain.reason.enlace_roto',
  falta_una_fila: 'audit.chain.reason.falta_una_fila',
  genesis_incorrecto: 'audit.chain.reason.genesis_incorrecto',
  hash_version_desconocida: 'audit.chain.reason.hash_version_desconocida',
};

export function AuditPage(): ReactNode {
  const [filters, setFilters] = useState<AuditListFilters>({
    limit: PAGE_SIZE,
    entityType: null,
    action: null,
  });

  const verification = useApiQuery<Verification>(
    auditKeys.verify(null, null),
    '/api/v1/audit/verify',
    undefined,
    // Un barrido completo de la cadena: no se repite solo. Se dispara al
    // entrar y después sólo si alguien lo pide con el botón.
    { staleTime: Number.POSITIVE_INFINITY },
  );

  const entries = useKeysetQuery<AuditEntry>(
    auditKeys.list(filters),
    '/api/v1/audit',
    { entity_type: filters.entityType, action: filters.action },
    { limit: PAGE_SIZE },
  );

  return (
    <div className="mx-auto max-w-5xl">
      <AdminNav />
      <div className="space-y-4">
        <ChainCard query={verification} />
        <EntriesCard
          entries={entries}
          filters={filters}
          onFilters={setFilters}
          brokenSeq={verification.data?.ok === false ? (verification.data.first_bad_seq ?? null) : null}
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════ integridad de la cadena

function ChainCard({
  query,
}: {
  query: UseQueryResult<Verification, ApiError>;
}): ReactNode {
  const { t, fmt } = useI18n();
  const report = query.data;
  const broken = report !== undefined && !report.ok;

  return (
    <Card>
      <CardHeader
        title={t('audit.chain.title')}
        description={t('audit.description')}
        actions={
          report === undefined ? undefined : (
            <Badge tone={broken ? 'critical' : 'verified'}>
              {broken ? t('audit.chain.broken') : t('audit.chain.ok')}
            </Badge>
          )
        }
      />

      {query.isPending && <LoadingState rows={3} label={t('audit.chain.checking')} />}

      {query.isError && (
        <CardBody>
          <ErrorState
            error={query.error}
            onRetry={() => {
              void query.refetch();
            }}
          />
        </CardBody>
      )}

      {report !== undefined && (
        <CardBody className="space-y-4">
          {report.entries === 0 ? (
            <p className="text-sm text-ink-muted">{t('audit.chain.empty')}</p>
          ) : (
            <p
              className={cn('text-sm', broken ? 'font-medium text-critical' : 'text-ink')}
              // Que un lector de pantalla lo anuncie al llegar el resultado:
              // es el dato por el que se entra a esta pantalla.
              role={broken ? 'alert' : undefined}
            >
              {/* Los números van por `fmt`: es el separador de miles del
                  tenant, no el del navegador. */}
              {broken
                ? t('audit.chain.brokenDetail', {
                    broken: fmt.number(report.broken),
                    entries: fmt.number(report.entries),
                    seq: report.first_bad_seq ?? '—',
                  })
                : t('audit.chain.okDetail', { entries: fmt.number(report.entries) })}
            </p>
          )}

          {broken && report.reason !== null && report.reason !== undefined && (
            <p className="text-sm text-ink">
              <span className="font-medium">{t('audit.chain.reason')}: </span>
              {reasonText(report.reason, t)}
            </p>
          )}

          <dl className="grid gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs tracking-wide text-ink-subtle uppercase">
                {t('audit.chain.entries')}
              </dt>
              <dd className="text-sm text-ink">{fmt.number(report.entries)}</dd>
            </div>
            <div>
              <dt className="text-xs tracking-wide text-ink-subtle uppercase">
                {t('audit.chain.elapsed')}
              </dt>
              <dd className="text-sm text-ink">{fmt.number(report.elapsed_ms, 1)} ms</dd>
            </div>
            {report.entries > 0 && <ChainAnchor headSeq={report.head_seq ?? null} headHash={report.head_hash ?? null} />}
          </dl>

          <details className="text-sm">
            <summary className="cursor-pointer text-ink-muted">{t('audit.chain.formula')}</summary>
            <code className="mt-2 block overflow-x-auto rounded bg-surface-sunken p-3 font-mono text-xs break-all text-ink-muted">
              {report.hash_formula}
            </code>
          </details>

          <div className="flex items-center gap-3">
            <Button
              size="sm"
              onClick={() => {
                void query.refetch();
              }}
              disabled={query.isFetching}
            >
              {query.isFetching ? t('audit.chain.rechecking') : t('audit.chain.recheck')}
            </Button>
          </div>
        </CardBody>
      )}
    </Card>
  );
}

/**
 * El ancla que un auditor se anota. Sale del propio informe de verificación
 * (`head_seq` + `head_hash`) y no de `GET /audit/head`: son el mismo par y
 * pedirlo dos veces sólo agrega una consulta que puede quedar desincronizada
 * con lo que se acaba de verificar.
 */
function ChainAnchor({
  headSeq,
  headHash,
}: {
  headSeq: number | null;
  headHash: string | null;
}): ReactNode {
  const { t } = useI18n();
  if (headSeq === null || headHash === null) return null;

  return (
    <div className="sm:col-span-3">
      <dt className="text-xs tracking-wide text-ink-subtle uppercase">{t('audit.chain.anchor')}</dt>
      <dd className="mt-1">
        <code className="block overflow-x-auto rounded bg-surface-sunken p-3 font-mono text-xs break-all text-ink select-all">
          #{headSeq} · {headHash}
        </code>
        <p className="mt-1 text-xs text-ink-subtle">{t('audit.chain.anchorHint')}</p>
      </dd>
    </div>
  );
}

// ═══════════════════════════════════════════════════ listado de entradas

function EntriesCard({
  entries,
  filters,
  onFilters,
  brokenSeq,
}: {
  entries: KeysetResult<AuditEntry>;
  filters: AuditListFilters;
  onFilters: (next: AuditListFilters) => void;
  brokenSeq: number | null;
}): ReactNode {
  const { t } = useI18n();
  const { query } = entries;
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <Card>
      <CardHeader title={t('audit.title')} description={t('audit.description')} />

      <CardBody className="border-b border-border">
        <AuditFilters filters={filters} onFilters={onFilters} />
      </CardBody>

      {query.isPending && <LoadingState rows={5} />}

      {query.isError && (
        <CardBody>
          <ErrorState
            error={query.error}
            onRetry={() => {
              void query.refetch();
            }}
          />
        </CardBody>
      )}

      {!query.isPending && !query.isError && entries.items.length === 0 && (
        <EmptyState title={t('audit.empty')} />
      )}

      {entries.items.length > 0 && (
        <>
          <Table caption={t('audit.description')}>
            <thead>
              <tr>
                <Th>{t('audit.column.seq')}</Th>
                <Th>{t('audit.column.when')}</Th>
                <Th>{t('audit.column.action')}</Th>
                <Th>{t('audit.column.entity')}</Th>
                <Th>{t('audit.column.actor')}</Th>
              </tr>
            </thead>
            <tbody>
              {entries.items.map((entry) => (
                <EntryRows
                  key={entry.seq}
                  entry={entry}
                  isBroken={brokenSeq === entry.seq}
                  isOpen={expanded === entry.seq}
                  onToggle={() => {
                    setExpanded((current) => (current === entry.seq ? null : entry.seq));
                  }}
                />
              ))}
            </tbody>
          </Table>

          <CardBody className="flex items-center justify-center border-t border-border">
            {entries.hasMore ? (
              <Button size="sm" onClick={entries.loadMore} disabled={entries.isLoadingMore}>
                {entries.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
              </Button>
            ) : (
              <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
            )}
          </CardBody>
        </>
      )}
    </Card>
  );
}

function AuditFilters({
  filters,
  onFilters,
}: {
  filters: AuditListFilters;
  onFilters: (next: AuditListFilters) => void;
}): ReactNode {
  const { t } = useI18n();
  const [entityType, setEntityType] = useState(filters.entityType ?? '');
  const [action, setAction] = useState(filters.action ?? '');

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onFilters({
      limit: filters.limit,
      entityType: entityType.trim() === '' ? null : entityType.trim(),
      action: action.trim() === '' ? null : action.trim(),
    });
  };

  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
      <label className="text-sm">
        <span className="block text-ink-muted">{t('audit.filter.entityType')}</span>
        <input
          value={entityType}
          onChange={(event) => {
            setEntityType(event.target.value);
          }}
          placeholder={t('audit.filter.entityTypeHint')}
          className="mt-1 w-56 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
        />
      </label>
      <label className="text-sm">
        <span className="block text-ink-muted">{t('audit.filter.action')}</span>
        <input
          value={action}
          onChange={(event) => {
            setAction(event.target.value);
          }}
          className="mt-1 w-56 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
        />
      </label>
      <Button type="submit" size="sm" variant="primary">
        {t('audit.filter.apply')}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setEntityType('');
          setAction('');
          onFilters({ limit: filters.limit, entityType: null, action: null });
        }}
      >
        {t('audit.filter.clear')}
      </Button>
    </form>
  );
}

function EntryRows({
  entry,
  isBroken,
  isOpen,
  onToggle,
}: {
  entry: AuditEntry;
  isBroken: boolean;
  isOpen: boolean;
  onToggle: () => void;
}): ReactNode {
  const { t, fmt } = useI18n();
  const detailsId = `audit-entry-${entry.seq}`;

  return (
    <>
      <tr className={cn(isBroken && 'bg-critical-soft')}>
        <Td className="font-mono">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-controls={detailsId}
            aria-label={t('audit.entry.details', { seq: entry.seq })}
            className="rounded font-mono text-accent underline-offset-2 hover:underline"
          >
            #{entry.seq}
          </button>
        </Td>
        <Td className="text-ink-muted">{fmt.dateTime(entry.created_at)}</Td>
        <Td className="font-medium">{entry.action}</Td>
        <Td className="text-ink-muted">{describeEntity(entry)}</Td>
        <Td className="text-ink-muted">{entry.actor_email ?? t('audit.entry.system')}</Td>
      </tr>
      {isBroken && (
        <tr className="bg-critical-soft">
          <td colSpan={5} className="border-b border-border px-4 pb-3">
            <Badge tone="critical">{t('audit.entry.broken')}</Badge>
          </td>
        </tr>
      )}
      {isOpen && (
        <tr id={detailsId}>
          <td colSpan={5} className="border-b border-border bg-surface-sunken px-4 py-3">
            <dl className="space-y-2 text-xs">
              <Field label={t('audit.entry.payload')} value={entry.payload} />
              <Field label={t('audit.entry.prevHash')} value={entry.prev_hash} />
              <Field label={t('audit.entry.rowHash')} value={entry.row_hash} />
              <Field label={t('audit.entry.hashVersion')} value={String(entry.hash_version)} />
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}

/** El motivo traducido, o el que informe el servidor si es uno nuevo. */
function reasonText(reason: string, t: (key: MessageKey, vars?: Record<string, string | number>) => string): string {
  const key = REASON_KEYS[reason];
  return key === undefined ? t('audit.chain.reason.other', { reason }) : t(key);
}

function Field({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="tracking-wide text-ink-subtle uppercase">{label}</dt>
      <dd>
        <code className="block overflow-x-auto font-mono break-all text-ink-muted">{value}</code>
      </dd>
    </div>
  );
}

/**
 * `unit` no lleva su id: el identificador interno de una unidad no se
 * renderiza nunca, tampoco acá. Hoy ninguna entrada usa ese tipo, pero la
 * regla se aplica en el punto donde se dibuja, no donde se recuerda.
 */
function describeEntity(entry: AuditEntry): string {
  if (entry.entity_type === 'unit') return entry.entity_type;
  const id = entry.entity_id;
  return id === null || id === undefined ? entry.entity_type : `${entry.entity_type} #${id}`;
}
